import Box from "@mui/material/Box";
import {NewLogoIconWithText} from "../NewHome/HomeIcons";
import {Button, Collapse, DialogContent, Typography} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, {useContext, useEffect, useState} from "react";
import Dialog from "@mui/material/Dialog";
import {AuthContext} from "../../context/AuthContext";
import V2LoginForm from "./V2LoginForm";
import V2SignupMessage from "./V2SignupMessage";
import V2SignupForm from "./V2SignupForm";
import {useLocation, useNavigate} from "react-router-dom";
import {trackLandingEvent} from "../../analytics/landingEvents";
import {LibrettoPlan} from "../../utils/utils";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PodcastsOutlinedIcon from "@mui/icons-material/PodcastsOutlined";
import OndemandVideoOutlinedIcon from "@mui/icons-material/OndemandVideoOutlined";
import VideoCameraFrontOutlinedIcon from "@mui/icons-material/VideoCameraFrontOutlined";
import CastForEducationOutlinedIcon from "@mui/icons-material/CastForEducationOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------

const defaultSignupPlan = LibrettoPlan.StudioFree;
const signupPlanQueryValueByPlan: Record<number, string> = {
    [LibrettoPlan.StudioFree]: "free",
    [LibrettoPlan.StudioMonthly]: "studio",
    [LibrettoPlan.StudioAnnual]: "studio-annual",
    [LibrettoPlan.StudioLifetime]: "studio-lifetime",
    [LibrettoPlan.StudioProMonthly]: "studio-pro",
};

const resolveSignupPlanFromOptions = (options?: any) => {
    if (!options || typeof options !== "object") return defaultSignupPlan;
    const rawPlan = options.librettoPlan ?? options.LibrettoPlan ?? options.plan;
    const parsedPlan = typeof rawPlan === "string" && rawPlan.trim() !== "" ? Number(rawPlan) : rawPlan;
    return Object.prototype.hasOwnProperty.call(signupPlanQueryValueByPlan, parsedPlan) ? parsedPlan : defaultSignupPlan;
};

const signupPlanQueryValueForPlan = (plan: number) =>
    signupPlanQueryValueByPlan[plan] || signupPlanQueryValueByPlan[defaultSignupPlan];

const resolveSignupPlanFromSearch = (search: string) => {
    const params = new URLSearchParams(search);
    const planValue = params.get("plan") || "";
    const signupPlanByQueryValue: Record<string, number> = {
        free: LibrettoPlan.StudioFree,
        studio: LibrettoPlan.StudioMonthly,
        "studio-annual": LibrettoPlan.StudioAnnual,
        "studio-lifetime": LibrettoPlan.StudioLifetime,
        "studio-pro": LibrettoPlan.StudioProMonthly,
    };
    return signupPlanByQueryValue[planValue] ?? defaultSignupPlan;
};

// ---------------------------------------------------------------------------
// Reusable small components
// ---------------------------------------------------------------------------

const V2SigninButton = ({onClick}: {onClick: () => void}) => (
    <Button
        variant="text"
        disableRipple
        sx={{
            backgroundColor: "transparent",
            color: "black",
            height: "36px",
            borderRadius: "999px",
            padding: "8px 14px",
            textTransform: "uppercase",
            fontFamily: '"Space Mono", monospace',
            fontWeight: 400,
            fontSize: "15px",
            lineHeight: "22px",
            letterSpacing: "-1px",
            whiteSpace: "nowrap",
            border: "1px solid transparent",
            "&:hover": {backgroundColor: "transparent", borderColor: "rgba(0,0,0,0.18)", opacity: 0.75},
        }}
        onClick={onClick}
    >
        Sign In
    </Button>
);

const V2GetStartedButton = ({onClick}: {onClick: (e?: any) => void}) => (
    <Button
        variant="contained"
        sx={{
            backgroundColor: "black",
            color: "white",
            height: "30px",
            borderRadius: "10px",
            padding: "13px 14px",
            textTransform: "uppercase",
            fontFamily: '"Space Mono", monospace',
            fontWeight: 700,
            fontSize: "14px",
            lineHeight: "22px",
            letterSpacing: "-0.8px",
            boxShadow: "0px 1px 4px 0px rgba(0,0,0,0.25)",
            whiteSpace: "nowrap",
            "&:hover": {backgroundColor: "#333"},
        }}
        onClick={onClick}
    >
        Get Started
    </Button>
);

const PrimaryCta = ({onClick, label = "Create Account"}: {onClick: () => void; label?: string}) => (
    <Button
        variant="contained"
        onClick={onClick}
        sx={{
            backgroundColor: "black",
            color: "white",
            height: {xs: "56px", md: "60px"},
            borderRadius: "14px",
            padding: {xs: "16px 42px", md: "18px 52px"},
            textTransform: "none",
            fontFamily: '"Inter", sans-serif',
            fontWeight: 600,
            fontSize: {xs: "17px", md: "18px"},
            letterSpacing: "-0.3px",
            boxShadow: "0px 2px 8px rgba(0,0,0,0.2)",
            whiteSpace: "nowrap",
            transition: "background-color 200ms ease, transform 200ms ease, box-shadow 200ms ease",
            "&:hover": {
                backgroundColor: "#222",
                transform: "translateY(-1px)",
                boxShadow: "0px 4px 16px rgba(0,0,0,0.25)",
            },
        }}
    >
        {label}
    </Button>
);

const SecondaryCta = ({onClick, label = "Watch 60-second demo"}: {onClick: () => void; label?: string}) => (
    <Button
        variant="text"
        disableRipple
        onClick={onClick}
        sx={{
            fontFamily: '"Space Mono", monospace',
            fontWeight: 700,
            fontSize: "14px",
            letterSpacing: "-0.5px",
            textTransform: "uppercase",
            color: "black",
            opacity: 0.7,
            "&:hover": {opacity: 1, backgroundColor: "transparent"},
        }}
    >
        {label}
    </Button>
);

const SectionTitle = ({children}: {children: React.ReactNode}) => (
    <Typography
        sx={{
            fontFamily: '"Hedvig Letters Serif", serif',
            fontSize: {xs: "28px", md: "48px"},
            lineHeight: {xs: "34px", md: "56px"},
            letterSpacing: {xs: "-1.5px", md: "-2.5px"},
            fontWeight: 400,
            color: "black",
            textAlign: "center",
        }}
    >
        {children}
    </Typography>
);

const SectionSubtitle = ({children}: {children: React.ReactNode}) => (
    <Typography
        sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: {xs: "15px", md: "18px"},
            lineHeight: 1.65,
            fontWeight: 400,
            color: "rgba(0,0,0,0.55)",
            textAlign: "center",
            maxWidth: "640px",
        }}
    >
        {children}
    </Typography>
);

const IconBox = ({children, color = "rgba(0,0,0,0.04)"}: {children: React.ReactNode; color?: string}) => (
    <Box
        sx={{
            width: 56,
            height: 56,
            borderRadius: "14px",
            backgroundColor: color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "black",
            flexShrink: 0,
        }}
    >
        {children}
    </Box>
);

// ---------------------------------------------------------------------------
// FAQ
// ---------------------------------------------------------------------------

const faqItems = [
    {
        q: "What kinds of recordings work best?",
        a: "Podcasts, interviews, talking-head videos, webinars, courses, and other long spoken recordings.",
    },
    {
        q: "Do I need to use the full editor?",
        a: "No. You can generate clip drafts and refine them quickly. The full editor is there if you want more control.",
    },
    {
        q: "Can I edit the suggested clips?",
        a: "Yes. You can review them in transcript, adjust boundaries, tighten pacing, and style captions before exporting.",
    },
    {
        q: "Does this work for multi-speaker recordings?",
        a: "Yes. It is designed for spoken-content workflows including interviews and multi-speaker conversations.",
    },
    {
        q: "Can I export for Shorts, Reels, and TikTok?",
        a: "Yes. Export vertical and square formats ready for all major short-form platforms.",
    },
    {
        q: "Can I start from an existing recording?",
        a: "Yes. Upload audio or video and generate clips from it.",
    },
];

const FaqItem = ({q, a}: {q: string; a: string}) => {
    const [open, setOpen] = useState(false);
    return (
        <Box
            onClick={() => setOpen(!open)}
            sx={{
                cursor: "pointer",
                borderBottom: "1px solid rgba(0,0,0,0.08)",
                py: {xs: 2.5, md: 3},
            }}
        >
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2}}>
                <Typography
                    sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 600,
                        fontSize: {xs: "15px", md: "17px"},
                        lineHeight: 1.4,
                        color: "black",
                    }}
                >
                    {q}
                </Typography>
                <KeyboardArrowDownIcon
                    sx={{
                        fontSize: 22,
                        color: "rgba(0,0,0,0.35)",
                        transform: open ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 200ms ease",
                        flexShrink: 0,
                    }}
                />
            </Box>
            <Collapse in={open}>
                <Typography
                    sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 400,
                        fontSize: {xs: "14px", md: "16px"},
                        lineHeight: 1.65,
                        color: "rgba(0,0,0,0.55)",
                        mt: 1.5,
                        maxWidth: "720px",
                    }}
                >
                    {a}
                </Typography>
            </Collapse>
        </Box>
    );
};

// ---------------------------------------------------------------------------
// Section components
// ---------------------------------------------------------------------------

const HeroSection = ({isMobile, onCta}: {isMobile: boolean; onCta: () => void}) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: {xs: "column", md: "row"},
            alignItems: {xs: "center", md: "center"},
            gap: {xs: "36px", md: "64px"},
            maxWidth: 1200,
            mx: "auto",
            pt: {xs: "48px", md: "80px"},
            pb: {xs: "24px", md: "48px"},
        }}
    >
        {/* Left: copy + CTAs */}
        <Box
            sx={{
                flex: {xs: "1 1 100%", md: "1 1 0"},
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: {xs: "20px", md: "28px"},
                alignItems: {xs: "center", md: "flex-start"},
                textAlign: {xs: "center", md: "left"},
            }}
        >
            <Typography
                sx={{
                    fontFamily: "Arial, sans-serif",
                    fontSize: {xs: "36px", sm: "44px", md: "56px"},
                    lineHeight: 1.08,
                    letterSpacing: {xs: "-1.5px", sm: "-2px", md: "-3px"},
                    fontWeight: 400,
                    color: "black",
                }}
            >

                Generate short clips from podcasts and interviews.{" "}
                <Box
                    component="span"
                    sx={{
                        background: "linear-gradient(135deg, #4ECDC4 0%, #44B09E 40%, #93C572 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                    }}
                >
                    Then refine them in transcript.
                </Box>
            </Typography>

            <Typography
                sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 400,
                    fontSize: {xs: "16px", md: "19px"},
                    lineHeight: 1.6,
                    color: "rgba(0,0,0,0.55)",
                    maxWidth: "520px",
                }}
            >
                Upload a podcast, interview, webinar, or talking-head video. Libretto finds strong clip
                candidates, shows them in transcript, and gives you real control to tighten, caption, and
                export polished shorts.
            </Typography>

            <Box sx={{display: "flex", flexDirection: {xs: "column", sm: "row"}, alignItems: "center", gap: {xs: 1.5, sm: 2.5}}}>
                <PrimaryCta onClick={onCta} />
                <SecondaryCta onClick={() => {}} label="Watch 60-second demo" />
            </Box>

            <Typography
                sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 400,
                    fontSize: "13px",
                    color: "rgba(0,0,0,0.35)",
                    letterSpacing: "-0.2px",
                }}
            >
                Built for podcasts, interviews, and talking videos
            </Typography>
        </Box>

        {/* Right: product visual placeholder */}
        <Box
            sx={{
                flex: {xs: "1 1 100%", md: "1 1 0"},
                minWidth: 0,
                width: "100%",
                maxWidth: {xs: "100%", md: "560px"},
                aspectRatio: "16/10",
                borderRadius: {xs: "14px", md: "16px"},
                border: "1px solid rgba(0,0,0,0.08)",
                backgroundColor: "#F6F5F4",
                boxShadow: "0px 8px 24px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Typography
                sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: "14px",
                    color: "rgba(0,0,0,0.3)",
                    fontWeight: 500,
                }}
            >
                Product visual
            </Typography>
        </Box>
    </Box>
);

const WorkflowStripSection = () => {
    const steps = [
        {icon: <UploadFileOutlinedIcon sx={{fontSize: 26}} />, title: "Upload", desc: "Upload a full podcast, interview, webinar, or talking-head video."},
        {icon: <AutoAwesomeOutlinedIcon sx={{fontSize: 26}} />, title: "Get clip drafts", desc: "Libretto suggests promising clip moments from your recording."},
        {icon: <TuneOutlinedIcon sx={{fontSize: 26}} />, title: "Review and refine", desc: "See each clip in transcript, trim starts, tighten pauses, remove fillers, and style captions."},
        {icon: <FileDownloadOutlinedIcon sx={{fontSize: 26}} />, title: "Export", desc: "Export polished vertical or square clips ready for Shorts, Reels, and TikTok."},
    ];

    return (
        <Box
            component="section"
            sx={{
                width: "100%",
                py: {xs: "56px", md: "80px"},
                backgroundColor: "#FAFAFA",
            }}
        >
            <Box sx={{maxWidth: 1100, mx: "auto", px: {xs: 2, md: 3.5}}}>
                <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", mb: {xs: "32px", md: "48px"}}}>
                    <SectionTitle>How it works</SectionTitle>
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "1fr", sm: "repeat(2, 1fr)", md: `repeat(${steps.length}, 1fr)`},
                        gap: {xs: "20px", md: "24px"},
                    }}
                >
                    {steps.map((step, i) => (
                        <Box
                            key={i}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                textAlign: "center",
                                gap: "14px",
                                px: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: "14px",
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "black",
                                }}
                            >
                                {step.icon}
                            </Box>
                            <Box sx={{display: "flex", flexDirection: "column", gap: "6px"}}>
                                <Typography
                                    sx={{
                                        fontFamily: '"Inter", sans-serif',
                                        fontWeight: 600,
                                        fontSize: {xs: "15px", md: "16px"},
                                        lineHeight: 1.3,
                                        color: "black",
                                    }}
                                >
                                    {step.title}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: '"Inter", sans-serif',
                                        fontWeight: 400,
                                        fontSize: {xs: "13px", md: "14px"},
                                        lineHeight: 1.55,
                                        color: "rgba(0,0,0,0.5)",
                                    }}
                                >
                                    {step.desc}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

const DifferentiationSection = () => {
    const cards = [
        {
            icon: <SearchOutlinedIcon sx={{fontSize: 26}} />,
            title: "Find",
            desc: "AI suggests strong clip candidates from long spoken recordings.",
        },
        {
            icon: <EditOutlinedIcon sx={{fontSize: 26}} />,
            title: "Refine",
            desc: "Edit the transcript, clip boundaries, and pacing before you export.",
        },
        {
            icon: <CheckCircleOutlineIcon sx={{fontSize: 26}} />,
            title: "Finish",
            desc: "Add captions and export polished clips without restarting elsewhere.",
        },
    ];

    return (
        <Box component="section" sx={{width: "100%", py: {xs: "56px", md: "80px"}}}>
            <Box sx={{maxWidth: 1100, mx: "auto", px: {xs: 2, md: 3.5}}}>
                <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", mb: {xs: "32px", md: "48px"}}}>
                    <SectionTitle>More than clip generation</SectionTitle>
                    <SectionSubtitle>
                        Most clip tools stop at generating drafts. Libretto helps you finish them.
                    </SectionSubtitle>
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "1fr", md: "repeat(3, 1fr)"},
                        gap: {xs: "24px", md: "32px"},
                    }}
                >
                    {cards.map((card, i) => (
                        <Box
                            key={i}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "16px",
                                p: {xs: 3, md: 4},
                                borderRadius: "16px",
                                border: "1px solid rgba(0,0,0,0.06)",
                                backgroundColor: "#FAFAFA",
                            }}
                        >
                            <IconBox>{card.icon}</IconBox>
                            <Typography
                                sx={{
                                    fontFamily: '"Inter", sans-serif',
                                    fontWeight: 600,
                                    fontSize: {xs: "20px", md: "22px"},
                                    lineHeight: 1.25,
                                    letterSpacing: "-0.3px",
                                    color: "black",
                                }}
                            >
                                {card.title}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: '"Inter", sans-serif',
                                    fontWeight: 400,
                                    fontSize: {xs: "15px", md: "16px"},
                                    lineHeight: 1.6,
                                    color: "rgba(0,0,0,0.55)",
                                }}
                            >
                                {card.desc}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

const TranscriptRefinementSection = () => {
    const bullets = [
        "See each clip candidate as a highlighted section of the transcript",
        "Adjust clip start and end with transcript awareness",
        "Remove filler-heavy intros or awkward dead space",
        "Tighten pacing before export",
        "Keep full control after AI suggestions",
    ];

    return (
        <Box component="section" sx={{width: "100%", py: {xs: "56px", md: "80px"}, backgroundColor: "#FAFAFA"}}>
            <Box
                sx={{
                    maxWidth: 1100,
                    mx: "auto",
                    px: {xs: 2, md: 3.5},
                    display: "flex",
                    flexDirection: {xs: "column", md: "row"},
                    alignItems: {xs: "center", md: "center"},
                    gap: {xs: "36px", md: "64px"},
                }}
            >
                {/* Left: copy */}
                <Box
                    sx={{
                        flex: {xs: "1 1 100%", md: "1 1 0"},
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: "20px",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"Hedvig Letters Serif", serif',
                            fontSize: {xs: "26px", md: "40px"},
                            lineHeight: {xs: "32px", md: "48px"},
                            letterSpacing: {xs: "-1px", md: "-2px"},
                            fontWeight: 400,
                            color: "black",
                        }}
                    >
                        Refine clips in transcript, not just by dragging blindly
                    </Typography>
                    <Box component="ul" sx={{m: 0, pl: "20px", display: "flex", flexDirection: "column", gap: "10px"}}>
                        {bullets.map((b, i) => (
                            <Typography
                                component="li"
                                key={i}
                                sx={{
                                    fontFamily: '"Inter", sans-serif',
                                    fontWeight: 400,
                                    fontSize: {xs: "15px", md: "16px"},
                                    lineHeight: 1.6,
                                    color: "rgba(0,0,0,0.55)",
                                }}
                            >
                                {b}
                            </Typography>
                        ))}
                    </Box>
                </Box>

                {/* Right: product visual placeholder */}
                <Box
                    sx={{
                        flex: {xs: "1 1 100%", md: "1 1 0"},
                        minWidth: 0,
                        width: "100%",
                        aspectRatio: "4/3",
                        borderRadius: {xs: "14px", md: "16px"},
                        border: "1px solid rgba(0,0,0,0.08)",
                        backgroundColor: "#fff",
                        boxShadow: "0px 8px 24px rgba(0,0,0,0.06)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography sx={{fontFamily: '"Inter", sans-serif', fontSize: "14px", color: "rgba(0,0,0,0.3)", fontWeight: 500}}>
                        Transcript refinement visual
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const AudienceSection = () => {
    const audiences = [
        {icon: <PodcastsOutlinedIcon sx={{fontSize: 26}} />, title: "Podcasts", desc: "Turn long episodes into short, coherent promo clips."},
        {icon: <VideoCameraFrontOutlinedIcon sx={{fontSize: 26}} />, title: "Interviews", desc: "Extract the strongest moments from guest conversations."},
        {icon: <OndemandVideoOutlinedIcon sx={{fontSize: 26}} />, title: "Talking-head YouTube", desc: "Create Shorts from long solo videos without starting over."},
        {icon: <CastForEducationOutlinedIcon sx={{fontSize: 26}} />, title: "Webinars", desc: "Pull key takeaways from live sessions for social."},
        {icon: <SchoolOutlinedIcon sx={{fontSize: 26}} />, title: "Courses & education", desc: "Turn lectures into bite-sized lessons and teasers."},
        {icon: <RecordVoiceOverOutlinedIcon sx={{fontSize: 26}} />, title: "Founder & expert content", desc: "Clip thought leadership from long-form recordings."},
    ];

    return (
        <Box component="section" sx={{width: "100%", py: {xs: "56px", md: "80px"}}}>
            <Box sx={{maxWidth: 1100, mx: "auto", px: {xs: 2, md: 3.5}}}>
                <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", mb: {xs: "32px", md: "48px"}}}>
                    <SectionTitle>Built for spoken-content creators</SectionTitle>
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)"},
                        gap: {xs: "16px", md: "20px"},
                    }}
                >
                    {audiences.map((item, i) => (
                        <Box
                            key={i}
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "14px",
                                p: {xs: 2.5, md: 3},
                                borderRadius: "14px",
                                border: "1px solid rgba(0,0,0,0.06)",
                            }}
                        >
                            <Box sx={{color: "rgba(0,0,0,0.5)", mt: "2px"}}>{item.icon}</Box>
                            <Box sx={{display: "flex", flexDirection: "column", gap: "4px"}}>
                                <Typography
                                    sx={{
                                        fontFamily: '"Inter", sans-serif',
                                        fontWeight: 600,
                                        fontSize: {xs: "14px", md: "15px"},
                                        lineHeight: 1.3,
                                        color: "black",
                                    }}
                                >
                                    {item.title}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: '"Inter", sans-serif',
                                        fontWeight: 400,
                                        fontSize: {xs: "13px", md: "14px"},
                                        lineHeight: 1.5,
                                        color: "rgba(0,0,0,0.45)",
                                    }}
                                >
                                    {item.desc}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

const BeforeAfterSection = () => (
    <Box component="section" sx={{width: "100%", py: {xs: "56px", md: "80px"}, backgroundColor: "#FAFAFA"}}>
        <Box sx={{maxWidth: 1100, mx: "auto", px: {xs: 2, md: 3.5}}}>
            <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", mb: {xs: "32px", md: "48px"}}}>
                <SectionTitle>From full episode to publish-ready clips</SectionTitle>
                <SectionSubtitle>
                    AI gets you close. Libretto gets you finished.
                </SectionSubtitle>
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {xs: "1fr", md: "1fr 48px 1fr"},
                    gap: {xs: "24px", md: "0px"},
                    alignItems: "center",
                }}
            >
                {/* Before */}
                <Box
                    sx={{
                        p: {xs: 3, md: 4},
                        borderRadius: "16px",
                        border: "1px solid rgba(0,0,0,0.06)",
                        backgroundColor: "#fff",
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                    }}
                >
                    <Typography sx={{fontFamily: '"Inter", sans-serif', fontWeight: 600, fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.5px", color: "rgba(0,0,0,0.35)"}}>
                        Before
                    </Typography>
                    {["52-minute interview", "Raw, unedited transcript", "Long-form recording"].map((t, i) => (
                        <Box key={i} sx={{display: "flex", alignItems: "center", gap: "10px"}}>
                            <Box sx={{width: 6, height: 6, borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.15)", flexShrink: 0}} />
                            <Typography sx={{fontFamily: '"Inter", sans-serif', fontSize: {xs: "14px", md: "15px"}, color: "rgba(0,0,0,0.6)"}}>
                                {t}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Arrow */}
                <Box sx={{display: {xs: "none", md: "flex"}, alignItems: "center", justifyContent: "center"}}>
                    <Typography sx={{fontSize: "28px", color: "rgba(0,0,0,0.2)"}}>→</Typography>
                </Box>

                {/* After */}
                <Box
                    sx={{
                        p: {xs: 3, md: 4},
                        borderRadius: "16px",
                        border: "1px solid rgba(0,0,0,0.06)",
                        backgroundColor: "#fff",
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                    }}
                >
                    <Typography sx={{fontFamily: '"Inter", sans-serif', fontWeight: 600, fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.5px", color: "rgba(78,205,196,0.8)"}}>
                        After
                    </Typography>
                    {["6 clip candidates identified", "Polished captions applied", "Vertical exports, trimmed intros, tightened pacing"].map((t, i) => (
                        <Box key={i} sx={{display: "flex", alignItems: "center", gap: "10px"}}>
                            <CheckCircleOutlineIcon sx={{fontSize: 16, color: "#4ECDC4", flexShrink: 0}} />
                            <Typography sx={{fontFamily: '"Inter", sans-serif', fontSize: {xs: "14px", md: "15px"}, color: "rgba(0,0,0,0.6)"}}>
                                {t}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    </Box>
);

const FullEditorBridgeSection = () => (
    <Box component="section" sx={{width: "100%", py: {xs: "56px", md: "80px"}}}>
        <Box
            sx={{
                maxWidth: 780,
                mx: "auto",
                px: {xs: 2, md: 3.5},
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: "20px",
            }}
        >
            <SectionTitle>Need more than clips?</SectionTitle>
            <Typography
                sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 400,
                    fontSize: {xs: "15px", md: "17px"},
                    lineHeight: 1.65,
                    color: "rgba(0,0,0,0.55)",
                    maxWidth: "560px",
                }}
            >
                Open any generated clip in Libretto's full editor to keep refining with transcript and timeline tools.
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: {xs: "8px", md: "12px"},
                    mt: 1,
                }}
            >
                {["Deeper trimming and cleanup", "Transcript-based editing", "Captions and styling", "Full recording-to-editing workflow"].map((label, i) => (
                    <Box
                        key={i}
                        sx={{
                            px: 2,
                            py: 1,
                            borderRadius: "999px",
                            border: "1px solid rgba(0,0,0,0.08)",
                            fontFamily: '"Inter", sans-serif',
                            fontSize: {xs: "13px", md: "14px"},
                            fontWeight: 500,
                            color: "rgba(0,0,0,0.6)",
                        }}
                    >
                        {label}
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
);

const FaqSection = () => (
    <Box component="section" sx={{width: "100%", py: {xs: "56px", md: "80px"}, backgroundColor: "#FAFAFA"}}>
        <Box sx={{maxWidth: 780, mx: "auto", px: {xs: 2, md: 3.5}}}>
            <Box sx={{mb: {xs: "28px", md: "40px"}, textAlign: "center"}}>
                <SectionTitle>Frequently asked questions</SectionTitle>
            </Box>
            <Box sx={{borderTop: "1px solid rgba(0,0,0,0.08)"}}>
                {faqItems.map((item, i) => (
                    <FaqItem key={i} q={item.q} a={item.a} />
                ))}
            </Box>
        </Box>
    </Box>
);

const FinalCtaSection = ({onCta}: {onCta: () => void}) => (
    <Box
        component="section"
        sx={{
            width: "100%",
            py: {xs: "64px", md: "96px"},
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: {xs: "16px", md: "24px"},
            px: {xs: 2, md: 3.5},
        }}
    >
        <Typography
            sx={{
                fontFamily: '"Hedvig Letters Serif", serif',
                fontSize: {xs: "28px", md: "44px"},
                lineHeight: {xs: "34px", md: "52px"},
                letterSpacing: {xs: "-1.5px", md: "-2px"},
                fontWeight: 400,
                color: "black",
                maxWidth: "700px",
            }}
        >
            Turn your long recording into polished short clips
        </Typography>
        <Typography
            sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 400,
                fontSize: {xs: "15px", md: "18px"},
                lineHeight: 1.6,
                color: "rgba(0,0,0,0.55)",
                maxWidth: "520px",
            }}
        >
            Generate clip drafts fast. Refine them in transcript. Export with confidence.
        </Typography>
        <Box sx={{mt: 1, display: "flex", flexDirection: {xs: "column", sm: "row"}, alignItems: "center", gap: {xs: 1.5, sm: 2.5}}}>
            <PrimaryCta onClick={onCta} />
            <SecondaryCta onClick={() => {}} label="Watch demo" />
        </Box>
    </Box>
);

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

const Footer = () => (
    <Box
        sx={{
            width: "100%",
            borderTop: "1px solid rgba(0,0,0,0.06)",
            py: {xs: 3, md: 4},
            display: "flex",
            justifyContent: "center",
            px: {xs: 2, md: 3.5},
        }}
    >
        <Box sx={{maxWidth: 1200, width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between"}}>
            <NewLogoIconWithText />
            <Typography sx={{fontFamily: '"Inter", sans-serif', fontSize: "13px", color: "rgba(0,0,0,0.35)"}}>
                &copy; {new Date().getFullYear()} Libretto
            </Typography>
        </Box>
    </Box>
);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const ClipsLandingPage = () => {
    const authContext = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery("(max-width:900px)");

    useEffect(() => {
        document.title = "Libretto Clips | Turn Long Recordings Into Polished Short Clips";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute("content", "Generate clip drafts from podcasts, interviews, webinars, and talking videos. Refine them in transcript and export polished shorts with Libretto.");
        }
    }, []);

    const loginDialogOpen = authContext.loginFormOpen;
    const signupDialogOpen = authContext.signupFormOpen;

    const isLoginRoute = location.pathname === "/shorts/login";
    const isSignupRoute = location.pathname === "/shorts/signup";
    const isAuthModalRoute = isLoginRoute || isSignupRoute;

    useEffect(() => {
        if (isLoginRoute) {
            authContext.handleSignupFormClose();
            authContext.handleLoginFormOpen();
        } else if (isSignupRoute) {
            authContext.handleLoginFormClose();
            authContext.handleSignupFormOpen({
                librettoPlan: resolveSignupPlanFromSearch(location.search),
            });
        }
    }, [authContext, isLoginRoute, isSignupRoute, location.search]);

    const openLoginModal = () => {
        authContext.handleSignupFormClose();
        authContext.handleLoginFormOpen();
        if (!isLoginRoute) navigate("/shorts/login", {replace: isAuthModalRoute});
    };

    const openSignupModal = (options?: any) => {
        const librettoPlan = resolveSignupPlanFromOptions(options);
        const nextSignupPath = `/shorts/signup?plan=${signupPlanQueryValueForPlan(librettoPlan)}`;
        authContext.handleLoginFormClose();
        authContext.handleSignupFormOpen({librettoPlan});
        if (!isSignupRoute || location.search !== `?plan=${signupPlanQueryValueForPlan(librettoPlan)}`) {
            navigate(nextSignupPath, {replace: isAuthModalRoute});
        }
    };

    const closeLoginModal = () => {
        authContext.handleLoginFormClose();
        if (isAuthModalRoute) navigate("/shorts", {replace: true});
    };

    const closeSignupModal = () => {
        authContext.handleSignupFormClose();
        if (isAuthModalRoute) navigate("/shorts", {replace: true});
    };

    const closeSignupOpenLogin = () => {
        authContext.handleSignupFormClose();
        authContext.handleLoginFormOpen();
        navigate("/shorts/login", {replace: isAuthModalRoute});
    };

    const handleMainCta = () => {
        openSignupModal();
        trackLandingEvent("clips_cta_click_generate_clips");
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                backgroundColor: "#FFFFFF",
            }}
        >
            <Box sx={{flexGrow: 1, width: "100%", display: "flex", flexDirection: "column", alignItems: "center"}}>
                {/* Sticky header */}
                <Box
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 100,
                        width: "100%",
                        backgroundColor: "rgba(255,255,255,0.85)",
                        backdropFilter: "blur(12px)",
                        WebkitBackdropFilter: "blur(12px)",
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            width: "100%",
                            maxWidth: 1450,
                            padding: "20px",
                            position: "relative",
                        }}
                    >
                        <Box sx={{display: "flex", cursor: "pointer"}} onClick={() => navigate("/")}>
                            <NewLogoIconWithText />
                        </Box>

                        <Box sx={{display: "flex", alignItems: "center", gap: "12px", marginLeft: "auto"}}>
                            <V2SigninButton onClick={() => { openLoginModal(); trackLandingEvent("clips_cta_click_topbar_sign_in"); }} />
                            <V2GetStartedButton onClick={(e: any) => { openSignupModal(e); trackLandingEvent("clips_cta_click_topbar_get_started"); }} />
                        </Box>

                        {/* Login dialog */}
                        <Dialog open={loginDialogOpen} onClose={closeLoginModal} sx={{"& .MuiDialog-paper": {borderRadius: "20px"}}}>
                            <DialogContent sx={{borderRadius: "30px", display: "flex", justifyContent: "center", alignItems: "center", minWidth: {xs: "300px", md: "400px"}}}>
                                <V2LoginForm
                                    signinWithGoogle={authContext.signinWithGoogle}
                                    handleLoginFormClose={closeLoginModal}
                                    signinWithEmail={authContext.signinWithEmail}
                                    handleSignUpFormOpen={openSignupModal}
                                    sendPasswordResetEmailForUser={authContext.sendPasswordResetEmailForUser}
                                />
                            </DialogContent>
                        </Dialog>

                        {/* Signup message dialog */}
                        <Dialog open={authContext.signUpStateMessageOpen} onClose={authContext.handleSignupStateMessageClose}>
                            <DialogContent sx={{borderRadius: "30px", display: "flex", justifyContent: "center", alignItems: "center", width: {xs: "300px", md: "400px"}}}>
                                <V2SignupMessage signUpStateMessage={authContext.signUpStateMessage} />
                            </DialogContent>
                        </Dialog>

                        {/* Signup dialog */}
                        <Dialog open={signupDialogOpen} onClose={closeSignupModal} sx={{"& .MuiDialog-paper": {borderRadius: "20px"}}}>
                            <DialogContent sx={{borderRadius: "30px", display: "flex", justifyContent: "center", alignItems: "center", minWidth: {xs: "300px", md: "400px"}}}>
                                <V2SignupForm
                                    selectedPricingOption={"Annual"}
                                    signupWithGoogle={authContext.signupWithGoogle}
                                    closeSignupOpenLogin={closeSignupOpenLogin}
                                    signUpWithEmailAndPassword={authContext.signUpWithEmailAndPassword}
                                />
                            </DialogContent>
                        </Dialog>
                    </Box>
                </Box>

                {/* Hero */}
                <Box sx={{width: "100%", px: {xs: 2, md: 4}, pt: {xs: 2, md: 4}, pb: 0}}>
                    <HeroSection isMobile={isMobile} onCta={handleMainCta} />
                </Box>

                {/* Sections */}
                <WorkflowStripSection />
                <DifferentiationSection />
                <TranscriptRefinementSection />
                <AudienceSection />
                <BeforeAfterSection />
                <FullEditorBridgeSection />
                <FaqSection />
                <FinalCtaSection onCta={handleMainCta} />
            </Box>

            <Footer />
        </Box>
    );
};

export default ClipsLandingPage;
